/**
 * Notification — Toast component for success/error feedback.
 *
 * Auto-dismisses after `duration` ms. Shows at top-right of the viewport.
 */

import { useEffect } from 'react';
import { X, CheckCircle, AlertCircle } from 'lucide-react';

const VARIANTS = {
    success: {
        icon: CheckCircle,
        border: 'border-emerald-200 dark:border-emerald-800',
        iconColor: 'text-emerald-500',
        bg: 'bg-white dark:bg-zinc-900',
    },
    error: {
        icon: AlertCircle,
        border: 'border-red-200 dark:border-red-800',
        iconColor: 'text-red-500',
        bg: 'bg-white dark:bg-zinc-900',
    },
};

export function Notification({ type = 'success', message, onDismiss, duration = 4000 }) {
    const v = VARIANTS[type] ?? VARIANTS.success;
    const Icon = v.icon;

    useEffect(() => {
        if (!duration) return;
        const timer = setTimeout(onDismiss, duration);
        return () => clearTimeout(timer);
    }, [duration, onDismiss]);

    return (
        <div
            role="status"
            aria-live="polite"
            className={`fixed top-4 right-4 z-50 flex items-start gap-3 px-4 py-3 rounded-lg border shadow-lg animate-slide-up ${v.border} ${v.bg}`}
        >
            <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${v.iconColor}`} />
            <p className="text-sm text-zinc-800 dark:text-zinc-200 pr-6">{message}</p>
            <button
                type="button"
                onClick={onDismiss}
                className="absolute top-2 right-2 p-1 rounded text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
                aria-label="Dismiss notification"
            >
                <X className="w-3.5 h-3.5" />
            </button>
        </div>
    );
}
